// Shared helpers that turn the "hours" collection (one document per weekday,
// with { day, isOpen, opening }) into rules a react-datepicker can use.
//
// Used by:
//   - src/Pages/Book.js                      (public booking form)
//   - src/Components/ManageReservations.js   (admin "Προσθήκη Κράτησης")
//
// Everything is computed in the browser's local time, which is what the
// date picker itself works in. The restaurant and its admins are in Greece,
// so this matches the intended times.

export const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Used when a day is marked open but its opening text is missing or written
// in a format we cannot read (e.g. "12μμ-11μμ"). Chosen so a typo in the
// working hours never blocks bookings completely.
export const FALLBACK_WINDOW = { start: 19 * 60, end: 22 * 60 };

// Parses "12:00 - 23:00" (tolerant of spacing, hyphen / en-dash / em-dash /
// "to" / "έως", and a "." separator) into minutes since midnight.
// If the end is not after the start the window is treated as crossing
// midnight, so end is pushed past 1440. Returns null if unparseable.
export function parseOpeningRange(opening) {
  if (!opening) return null;
  const m = String(opening).match(
    /(\d{1,2})[:.](\d{2})\s*(?:-|–|—|to|έως)\s*(\d{1,2})[:.](\d{2})/i
  );
  if (!m) return null;

  const startH = parseInt(m[1], 10);
  const endH = parseInt(m[3], 10);
  const start = startH * 60 + parseInt(m[2], 10);
  let end = endH * 60 + parseInt(m[4], 10);
  if ([start, end].some((v) => Number.isNaN(v)) || startH > 23 || endH > 23) {
    return null;
  }
  if (end <= start) end += 24 * 60; // overnight, e.g. 18:00 - 01:00
  return { start, end };
}

function findDay(hours, name) {
  if (!name || !Array.isArray(hours)) return null;
  const target = name.trim().toLowerCase();
  return (
    hours.find((d) => (d.day || "").trim().toLowerCase() === target) || null
  );
}

// The bookable windows of a given calendar date, in minutes since that
// date's midnight. Includes any late-night service that started the previous
// evening and runs past midnight.
export function windowsForDate(date, hours) {
  if (!(date instanceof Date) || isNaN(date)) return [];
  // Hours not loaded yet (or empty): don't lock the user out.
  if (!Array.isArray(hours) || hours.length === 0) return [{ ...FALLBACK_WINDOW }];

  const idx = date.getDay();
  const today = findDay(hours, DAY_NAMES[idx]);
  const prev = findDay(hours, DAY_NAMES[(idx + 6) % 7]);
  const windows = [];

  // A day with no record at all falls back rather than silently closing.
  if (!today || today.isOpen) {
    const r = (today && parseOpeningRange(today.opening)) || FALLBACK_WINDOW;
    windows.push({ start: r.start, end: Math.min(r.end, 24 * 60) });
  }

  // Spill-over from the previous night (e.g. yesterday 18:00 - 01:00).
  if (prev && prev.isOpen) {
    const r = parseOpeningRange(prev.opening);
    if (r && r.end > 24 * 60) {
      windows.push({ start: 0, end: r.end - 24 * 60, spill: true });
    }
  }

  return windows.sort((a, b) => a.start - b.start);
}

const minutesOf = (d) => d.getHours() * 60 + d.getMinutes();

const atMinutes = (date, mins) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setMinutes(mins);
  return d;
};

// Can a table be booked at this exact date+time?
// A booking must start while the restaurant is open (strictly before closing)
// and at least `leadMinutes` from now.
export function isTimeBookable(dateTime, hours, leadMinutes = 30) {
  if (!(dateTime instanceof Date) || isNaN(dateTime)) return false;
  if (dateTime.getTime() < Date.now() + leadMinutes * 60 * 1000) return false;
  const m = minutesOf(dateTime);
  return windowsForDate(dateTime, hours).some((w) => m >= w.start && m < w.end);
}

// Is any slot still bookable on this day?
export function isDateBookable(date, hours, leadMinutes = 30) {
  const windows = windowsForDate(date, hours);
  if (windows.length === 0) return false;
  const cutoff = Date.now() + leadMinutes * 60 * 1000;
  // The last minute you could still book on this day.
  return windows.some((w) => atMinutes(date, w.end - 1).getTime() >= cutoff);
}

// First/last times to show in the picker's time column for a given day.
// react-datepicker builds the list from these, and isTimeBookable() then
// greys out anything in between that falls outside the opening windows.
export function dayTimeBounds(date, hours, intervalMinutes = 30) {
  const base = date instanceof Date && !isNaN(date) ? date : new Date();
  const windows = windowsForDate(base, hours);
  if (windows.length === 0) {
    return { minTime: atMinutes(base, 0), maxTime: atMinutes(base, 23 * 60 + 30) };
  }
  const start = Math.min(...windows.map((w) => w.start));
  const end = Math.max(...windows.map((w) => w.end));
  const last = Math.max(start, end - intervalMinutes);
  return { minTime: atMinutes(base, start), maxTime: atMinutes(base, last) };
}

// After the user picks a day, move the time to the first slot that is
// actually bookable, so the field never shows a closed-hours time.
export function firstValidSlot(date, hours, {
  intervalMinutes = 30,
  leadMinutes = 30,
} = {}) {
  // Prefer the day's own service over an after-midnight leftover from the
  // previous evening, so clicking a day lands on its normal opening time.
  const windows = windowsForDate(date, hours).sort(
    (a, b) => (a.spill ? 1 : 0) - (b.spill ? 1 : 0)
  );
  const cutoff = Date.now() + leadMinutes * 60 * 1000;
  for (const w of windows) {
    // Round the window start up to the next interval boundary.
    let m = Math.ceil(w.start / intervalMinutes) * intervalMinutes;
    for (; m < w.end; m += intervalMinutes) {
      const candidate = atMinutes(date, m);
      if (candidate.getTime() >= cutoff) return candidate;
    }
  }
  return null;
}

// Human-readable opening text for a day, for hints under the picker.
export function openingLabelForDate(date, hours) {
  const windows = windowsForDate(date, hours);
  if (windows.length === 0) return "";
  const fmt = (m) =>
    `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
  return windows
    .map((w) => `${fmt(w.start)} - ${fmt(w.end === 24 * 60 ? 0 : w.end)}`)
    .join(", ");
}
